
import React, { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { UserPlus } from 'lucide-react';
import { useAdminUsers } from '@/hooks/useAdminUsers';
import AdminUsersTable from './users/AdminUsersTable';
import UsersManagementHeader from './users/UsersManagementHeader';
import UserTabsList from './users/UserTabsList';
import UserTabContent from './users/UserTabContent';
import AdminUserSearch from './users/AdminUserSearch';
import InviteAdminForm from './users/InviteAdminForm';
import PlaceholderTabContent from './users/PlaceholderTabContent';

interface UsersManagementTabProps {
  instanceId: string;
}

const UsersManagementTab: React.FC<UsersManagementTabProps> = ({ instanceId }) => {
  const [activeTab, setActiveTab] = useState('all');
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();
  
  const {
    isLoading,
    filteredUsers,
    adminOnlyUsers,
    pendingInvitations,
    searchTerm,
    setSearchTerm,
    loadUsers,
    handleDeleteUser,
    handleInviteUser
  } = useAdminUsers(instanceId);

  const handleInvite = async (email: string, name: string, role: 'admin' | 'manager') => {
    setIsSubmitting(true);
    try {
      await handleInviteUser(email, name, role);
      setShowInviteForm(false);
    } catch (error) {
      console.error('Error inviting user:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <UsersManagementHeader onRefresh={loadUsers} />
        <CardContent>
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-semibold">User Management</h2>
            <Button 
              onClick={() => setShowInviteForm(!showInviteForm)}
              className="flex items-center"
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Invite User
            </Button>
          </div>
          
          {showInviteForm && (
            <InviteAdminForm
              onInvite={handleInvite}
              onCancel={() => setShowInviteForm(false)}
              isSubmitting={isSubmitting}
            />
          )}
          
          <AdminUserSearch 
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
          />
          
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <UserTabsList activeTab={activeTab} />
            
            <TabsContent value="all" className="space-y-4">
              <UserTabContent isLoading={isLoading}>
                <AdminUsersTable 
                  users={filteredUsers}
                  onDeleteUser={handleDeleteUser}
                />
              </UserTabContent>
            </TabsContent>
            
            <TabsContent value="admins" className="space-y-4">
              <UserTabContent isLoading={isLoading}>
                <AdminUsersTable 
                  users={adminOnlyUsers}
                  onDeleteUser={handleDeleteUser}
                />
              </UserTabContent>
            </TabsContent>
            
            <TabsContent value="pending" className="space-y-4">
              <UserTabContent isLoading={isLoading}>
                {pendingInvitations.length > 0 ? (
                  <AdminUsersTable 
                    users={pendingInvitations}
                    onDeleteUser={handleDeleteUser}
                  />
                ) : (
                  <PlaceholderTabContent message="No pending invitations found." />
                )}
              </UserTabContent>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersManagementTab;
